import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { usePermissions } from '@/contexts/PermissionContext';
import { useInstallPrompt } from '@/contexts/InstallPromptContext';
import { LayoutDashboard, PlusCircle, Receipt, BarChart3, LogOut, Settings, Users, Database, ChevronDown, Download, Menu, X } from 'lucide-react';
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Layout({ children }) {
  const location = useLocation();
  const { instance, accounts } = useMsal();
  const { hasPageAccess, userRoles, permissionsLoaded, loading } = usePermissions();
  const { isInstallable, handleInstallClick } = useInstallPrompt();
  const account = accounts[0];
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Map page names to paths
  const pagePathMap = {
    'Dashboard': '/',
    'MonthlyConsumption': '/consumption',
    'ConsolidatedInvoices': '/invoices',
    'EstimateForm': '/estimate/new',
    'InvoiceView': '/invoices/:id',
    'RoleManagement': '/admin/roles',
    'UserManagement': '/admin/users',
    'MasterData': '/admin/master-data',
  };

  console.log("Layout Debug:", { permissionsLoaded, loading, rolesCount: userRoles.length, roles: userRoles });

  const allNavItems = [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard', page: 'Dashboard' },
    { to: '/consumption', icon: BarChart3, label: 'Monthly Consumption', page: 'MonthlyConsumption' },
    { to: '/invoices', icon: Receipt, label: 'Invoices', page: 'ConsolidatedInvoices' },
    { to: '/estimate/new', icon: PlusCircle, label: 'New Estimate', page: 'EstimateForm' },
  ];

  const adminItems = [
    { to: '/admin/roles', icon: Settings, label: 'Manage Roles', page: 'RoleManagement' },
    { to: '/admin/users', icon: Users, label: 'Manage Users', page: 'UserManagement' },
    { to: '/admin/master-data', icon: Database, label: 'Master Data', page: 'MasterData' },
  ];

  // Filter navigation items based on user permissions
  // Show only items the user's assigned pages (from the API) grant access to
  const navItems = allNavItems.filter(item => {
    const canAccess = hasPageAccess(item.page);
    console.log(`Nav item '${item.label}' (${item.page}): hasAccess=${canAccess}`);
    return canAccess;
  });
  const accessibleAdminItems = adminItems.filter(item => {
    const canAccess = hasPageAccess(item.page);
    console.log(`Admin item '${item.label}' (${item.page}): hasAccess=${canAccess}`);
    return canAccess;
  });

  const handleSignOut = () => {
    instance.logoutRedirect();
  };

  return (
    <div className="min-h-screen bg-black" data-testid="app-layout">
      <header className="border-b border-zinc-900 bg-black/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-3 md:px-4 lg:px-8 flex items-center justify-between h-14 gap-1 sm:gap-2">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-1 group flex-shrink-0" data-testid="app-logo">
            <img src="/aays-logo.png" alt="Aays" className="h-6 sm:h-7 md:h-8 w-auto invert" />
            <span className="text-zinc-600 font-light text-xs sm:text-sm md:text-base hidden md:inline">|</span>
            <span className="font-manrope font-bold text-xs md:text-sm tracking-tight text-white group-hover:text-brand-400 transition-colors hidden md:inline">
              Revenue Management
            </span>
          </Link>

          {/* Hamburger Menu - Mobile/Tablet Only */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden flex items-center justify-center w-9 h-9 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-900 transition-all"
            data-testid="mobile-menu-toggle"
            title="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          {/* Desktop Navigation - Hidden on Mobile/Tablet */}
          <nav className="hidden lg:flex items-center gap-0.5 sm:gap-1" data-testid="main-nav">
            {/* Show skeleton loaders while permissions are loading */}
            {!permissionsLoaded ? (
              <>
                <Skeleton className="h-9 w-24 rounded-md bg-zinc-800" />
                <Skeleton className="h-9 w-32 rounded-md bg-zinc-800" />
                <Skeleton className="h-9 w-20 rounded-md bg-zinc-800" />
                <Skeleton className="h-9 w-28 rounded-md bg-zinc-800" />
              </>
            ) : (
              navItems.map(({ to, icon: Icon, label }) => {
                const isActive = location.pathname === to || 
                  (to === '/estimate/new' && location.pathname.startsWith('/estimate')) ||
                  (to === '/invoices' && location.pathname === '/invoices') ||
                  (to === '/consumption' && location.pathname === '/consumption');
                return (
                  <Link
                    key={to}
                    to={to}
                    data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
                    className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      isActive
                        ? 'text-brand-400 bg-brand-500/10'
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-900'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{label}</span>
                  </Link>
                );
              })
            )}
          </nav>

          {/* Right Side - Install, User Menu (Desktop Only) */}
          <div className="hidden lg:flex items-center gap-1 sm:gap-2">
            {/* Install App Button */}
            {isInstallable && (
              <button
                onClick={handleInstallClick}
                data-testid="install-app-btn"
                className="flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium text-white bg-brand-500/20 hover:bg-brand-500/30 border border-brand-500/30 hover:border-brand-500/50 transition-all duration-200 whitespace-nowrap flex-shrink-0"
                title="Install app for offline access"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Install</span>
              </button>
            )}

            {/* User Menu */}
            {account && (
              <div className="ml-2 sm:ml-4 pl-2 sm:pl-4 border-l border-zinc-800 flex-shrink-0">
                <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="user-menu-trigger"
                      className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium text-zinc-400 hover:text-white hover:bg-zinc-900 transition-all duration-200"
                    >
                      <span className="truncate max-w-[80px] sm:max-w-[120px]">{account.name || account.username}</span>
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-zinc-900 border-zinc-800">
                    {/* Show skeletons while loading permissions */}
                    {!permissionsLoaded && (
                      <>
                        <DropdownMenuLabel className="text-xs font-semibold text-zinc-500 uppercase tracking-wider px-2 py-2">
                          Loading...
                        </DropdownMenuLabel>
                        <div className="px-2 py-2 space-y-2">
                          <Skeleton className="h-8 w-full bg-zinc-800 rounded" />
                          <Skeleton className="h-8 w-full bg-zinc-800 rounded" />
                        </div>
                        <DropdownMenuSeparator className="bg-zinc-800" />
                      </>
                    )}

                    {/* Role Management Section - Show only if user has access to at least one admin page */}
                    {permissionsLoaded && accessibleAdminItems.length > 0 && (
                      <>
                        <DropdownMenuLabel className="text-xs font-semibold text-zinc-500 uppercase tracking-wider px-2 py-2">
                          Role Management
                        </DropdownMenuLabel>
                        
                        {accessibleAdminItems.map((item) => (
                          <Link key={item.to} to={item.to}>
                            <DropdownMenuItem className="cursor-pointer text-zinc-300 hover:text-orange-400 focus:bg-zinc-800 focus:text-orange-400">
                              <item.icon className="w-4 h-4 mr-2" />
                              <span>{item.label}</span>
                            </DropdownMenuItem>
                          </Link>
                        ))}
                        
                        <DropdownMenuSeparator className="bg-zinc-800" />
                      </>
                    )}
                    <DropdownMenuItem 
                      onClick={handleSignOut}
                      className="cursor-pointer text-zinc-300 hover:text-red-400 focus:bg-zinc-800 focus:text-red-400"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      <span>Sign out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Side Menu - Visible on Mobile/Tablet */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 lg:hidden z-40"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="mobile-menu-backdrop"
          />
          
          {/* Side Menu */}
          <div className="fixed left-0 top-14 bottom-0 w-64 bg-zinc-950 border-r border-zinc-900 z-40 overflow-y-auto lg:hidden" data-testid="mobile-menu">
            <nav className="p-4 space-y-2">
              {/* User Profile Section */}
              {account && (
                <div className="mb-4 pb-4 border-b border-zinc-800">
                  <div className="px-2 py-3">
                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Signed in as</p>
                    <p className="text-sm font-semibold text-white truncate">{account.name || account.username}</p>
                  </div>
                </div>
              )}

              {/* Navigation Items */}
              {!permissionsLoaded ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full rounded-md bg-zinc-800" />
                  <Skeleton className="h-10 w-full rounded-md bg-zinc-800" />
                  <Skeleton className="h-10 w-full rounded-md bg-zinc-800" />
                  <Skeleton className="h-10 w-full rounded-md bg-zinc-800" />
                </div>
              ) : (
                <>
                  {navItems.map(({ to, icon: Icon, label }) => {
                    const isActive = location.pathname === to || 
                      (to === '/estimate/new' && location.pathname.startsWith('/estimate')) ||
                      (to === '/invoices' && location.pathname === '/invoices') ||
                      (to === '/consumption' && location.pathname === '/consumption');
                    return (
                      <Link
                        key={to}
                        to={to}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                          isActive
                            ? 'text-brand-400 bg-brand-500/10 border border-brand-500/20'
                            : 'text-zinc-300 hover:text-white hover:bg-zinc-900'
                        }`}
                      >
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        <span>{label}</span>
                      </Link>
                    );
                  })}

                  {/* Admin Items */}
                  {accessibleAdminItems.length > 0 && (
                    <>
                      <div className="my-3 h-px bg-zinc-800" />
                      <div className="px-4 py-2 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                        Administration
                      </div>
                      {accessibleAdminItems.map((item) => {
                        const isActive = location.pathname === item.to;
                        return (
                          <Link
                            key={item.to}
                            to={item.to}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                              isActive
                                ? 'text-orange-400 bg-orange-500/10 border border-orange-500/20'
                                : 'text-zinc-300 hover:text-white hover:bg-zinc-900'
                            }`}
                          >
                            <item.icon className="w-5 h-5 flex-shrink-0" />
                            <span>{item.label}</span>
                          </Link>
                        );
                      })}
                    </>
                  )}

                  {/* Install App Button - Mobile */}
                  {isInstallable && (
                    <>
                      <div className="my-3 h-px bg-zinc-800" />
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          handleInstallClick();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white bg-brand-500/20 hover:bg-brand-500/30 border border-brand-500/30 hover:border-brand-500/50 transition-all duration-200"
                        data-testid="mobile-install-app-btn"
                        title="Install app for offline access"
                      >
                        <Download className="w-5 h-5 flex-shrink-0" />
                        <span>Install App</span>
                      </button>
                    </>
                  )}

                  {/* Sign Out */}
                  <div className="my-3 h-px bg-zinc-800" />
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleSignOut();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200"
                  >
                    <LogOut className="w-5 h-5 flex-shrink-0" />
                    <span>Sign Out</span>
                  </button>
                </>
              )}
            </nav>
          </div>
        </>
      )}

      <main className="max-w-7xl mx-auto px-3 sm:px-4 md:px-8 py-4 sm:py-6 md:py-8">
        {children}
      </main>
    </div>
  );
}
